import { createSelector } from "@reduxjs/toolkit"
import { RootState } from "./store"
import { MessageStatus } from "../lib/messageHelpers"
import { Chat } from "./chatsSlice"
import ChatHelpers from "../lib/chatHelpers"
import { getItem } from "./sendoutQueueSelectors"
import { selectAccountChats } from "./accountChatsSlice"
import { AccountChat } from "@/lib/accountChatsParser"
import { isDriverRoomGroup, isObuRoomGroup } from "@/lib/slickAISilence"

type MessageStatuses = Record<string, MessageStatus | undefined>

export const messageStatuses = (chat: Chat | undefined) =>
  createSelector(
    [
      (state: RootState) => state.sendoutQueue,
      (state: RootState) => state.messages,
      selectAccountChats,
    ],
    (sendoutQueue, messages, accountChats) => {
      const statuses: MessageStatuses = {}
      if (!chat) return statuses
      // OBU-bridged rooms have no driver_a but get displayed markers from
      // the forwarder when the driver reads the message on the on-board unit
      const hasDriverA =
        !!accountChats[chat.jid]?.driverA ||
        isObuRoomGroup(accountChats[chat.jid]?.group)
      const chatMessages = messages[chat.jid]?.messages || []

      for (const message of chatMessages) {
        const queueItem = getItem(message.id)(sendoutQueue)
        const ackStatus = queueItem
          ? queueItem.failed
            ? "not_sent"
            : "scheduled"
          : "sent"
        const id = message.id
        statuses[id] = ChatHelpers.messageStatus(
          chat,
          chatMessages,
          message,
          ackStatus,
          hasDriverA,
        )
      }
      return statuses
    },
  )

export const selectRouteJidFromChatJid = (chatJid: string) =>
  createSelector([selectAccountChats], (accountChats) => {
    return getRouteJidFromChatJid(chatJid, accountChats)
  })

export const getRouteJidFromChatJid = (
  chatJid: string | undefined,
  accountChats: Record<string, AccountChat>,
) => {
  if (!chatJid) {
    return undefined
  }

  const accountChat = accountChats[chatJid]
  if (!accountChat) {
    return undefined
  }

  if (isDriverRoomGroup(accountChat.group)) {
    return chatJid
  }

  return undefined
}

/**
 * Returns the JID used by the chat view to read driver location.
 * Location PubSub items are keyed by chat/accountChat JID for supported driver chats.
 */
export const selectLocationJidFromChatJid = (chatJid: string) =>
  createSelector([selectAccountChats], (accountChats) => {
    return getLocationJidFromChatJid(chatJid, accountChats)
  })

export const getLocationJidFromChatJid = (
  chatJid: string | undefined,
  accountChats: Record<string, AccountChat>,
) => {
  if (!chatJid) {
    return undefined
  }

  const accountChat = accountChats[chatJid]
  if (!accountChat) {
    return undefined
  }

  return chatJid
}
